import {type ReactNode, useMemo} from 'react';
import FavouriteButton from './FavouriteButton';
import type {Product} from './ProductResultTable';
import type {SearchResponseSettings} from './ProductSearchForm';
import normalizeImageSource from '../utils/normalizeImageSource';

interface ProductDetailSurfaceProps {
    product: Product;
    settings?: SearchResponseSettings;
    addToCart: ReactNode;
    imageLoading?: 'eager' | 'lazy';
}

const ProductDetailSurface: React.FC<ProductDetailSurfaceProps> = ({
    product,
    settings,
    addToCart,
    imageLoading = 'lazy',
}) => {
    const resolvedImage = useMemo(() => {
        const primaryImage = normalizeImageSource(product.image);
        if (primaryImage !== '') {
            return primaryImage;
        }

        return normalizeImageSource(product.placeholder_image);
    }, [product.image, product.placeholder_image]);

    const descriptionHtml = useMemo(() => {
        return String(product.description || '').trim();
    }, [product.description]);

    const productFiles = useMemo(() => {
        if (!Array.isArray(product.product_files)) {
            return [];
        }

        return product.product_files.filter((file) => {
            return file
                && typeof file === 'object'
                && String(file.url || '').trim() !== '';
        });
    }, [product.product_files]);

    return (
        <div className="find-products-single-product-view">
            <div className="find-products-single-product-media">
                <div className="find-products-single-product-image">
                    {settings?.show_favourite_button !== false && (
                        <FavouriteButton
                            productId={product.id}
                            initialIsFavourite={product.is_favourite === true}
                            displayMode="icon"
                            className="find-products-image-favourite"
                            canToggle={settings?.favourites_can_toggle === true}
                            ajaxUrl={settings?.favourites_ajax_url}
                            nonce={settings?.favourites_nonce}
                            groupsEnabled={settings?.favourites_groups_enabled === true}
                        />
                    )}
                    {resolvedImage !== '' ? (
                        <img
                            src={resolvedImage}
                            alt={String(product.name || 'Product image')}
                            loading={imageLoading}
                        />
                    ) : (
                        <div className="find-products-single-product-image-placeholder">
                            No image available
                        </div>
                    )}
                </div>
            </div>

            <div className="find-products-single-product-summary">
                {descriptionHtml !== '' && (
                    <div
                        className="find-products-single-product-description"
                        dangerouslySetInnerHTML={{__html: descriptionHtml}}
                    />
                )}

                <div className="find-products-single-product-add-to-cart weka-add-to-cart-surface weka-surface weka-surface--product-add-to-cart">
                    <div className="weka-add-to-cart-surface__inner">
                        {addToCart}
                    </div>
                </div>

                {productFiles.length > 0 && (
                    <section className="find-products-product-files portal-panel weka-surface weka-product-files">
                        <h2 className="portal-panel-header">Product Files</h2>
                        <p className="find-products-product-files__intro">
                            Download synced documents and product resources for this item.
                        </p>
                        <ul className="weka-product-files__list">
                            {productFiles.map((file) => {
                                const typeLabel = String(file.extension || file.mime_type || 'file')
                                    .replace(/^\./, '')
                                    .trim()
                                    .toUpperCase() || 'FILE';
                                const fileTitle = String(file.title || '').trim() || 'File';
                                const fileSize = String(file.file_size || '').trim();

                                return (
                                    <li key={`${file.guid || 'file'}-${file.attachment_id}`} className="weka-product-files__item">
                                        <div className="weka-product-files__details">
                                            <div className="weka-product-files__name">{fileTitle}</div>
                                            <div className="weka-product-files__meta">
                                                <span className="weka-product-files__type">{typeLabel}</span>
                                                {fileSize !== '' && (
                                                    <span className="weka-product-files__size">{fileSize}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="weka-product-files__actions">
                                            <a href={String(file.url || '')} className="button weka-primary-btn weka-product-files__link">
                                                Download
                                            </a>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ProductDetailSurface;
